/**
 * AuthProvider + useAuth + RoleGate (Sprint 13).
 *
 * Tenant-aware: JWT'de tenant_id + role + branch_id var.
 * Tenant switcher cache invalidate eder.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  is_superuser?: boolean;
}

export interface AuthTenant {
  id: string;
  slug: string;
  name: string;
  brand_color?: string;
}

export interface AuthBranch {
  id: string;
  name: string;
}

export interface Membership {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  role: string;
  branch_id: string | null;
}

export interface AuthState {
  user: AuthUser | null;
  tenant: AuthTenant | null;
  branch: AuthBranch | null;
  role: string | null;
  memberships: Membership[];
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string, tenantSlug?: string, app?: string) => Promise<void>;
  logout: () => void;
  switchTenant: (tenantSlug: string) => Promise<void>;
  permissions: Array<{ resource: string; actions: string[] }>;
  // Owner'ın bu rol için gizlediği admin menü anahtarları (PageKey listesi).
  // AppShell bir öğeyi göstermek için hem izin (permissions) hem de bu listede
  // OLMAMASI şartını arar. Yetkisi olmayan rollerde boş kalır.
  menuHidden: string[];
  // Auth state localStorage'dan senkron yüklenir (loadState), gerçek bir
  // async yükleme aşaması yok — bu yüzden her zaman false. Alan, tüketici
  // bileşenlerin (ör. POS App) yükleme guard'ı yazabilmesi için tip seviyesinde
  // mevcut; ileride token refresh/SSO eklenirse anlam kazanır.
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Permission/menu görünürlüğü localStorage'da cache'lenir — sayfa yenileme /
// cookie auto-login sonrası ilk render'da SENKRON yüklenir ki permission-bazlı
// menüler boş `[]` ile gizlenmesin (yarış: /api/auth/me/ async döner).
function loadCachedPerms(): {
  permissions: AuthContextValue["permissions"];
  menuHidden: string[];
} {
  try {
    const p = JSON.parse(localStorage.getItem("auth_permissions") || "[]");
    const m = JSON.parse(localStorage.getItem("auth_menu_hidden") || "[]");
    return { permissions: Array.isArray(p) ? p : [], menuHidden: Array.isArray(m) ? m : [] };
  } catch {
    return { permissions: [], menuHidden: [] };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => loadState());
  const cached = loadCachedPerms();
  const [permissions, setPermissions] = useState<AuthContextValue["permissions"]>(cached.permissions);
  const [menuHidden, setMenuHidden] = useState<string[]>(cached.menuHidden);
  // İlk yetki yüklemesi tamamlandı mı? Auth var ama henüz cache yoksa, menüyü
  // çizmeden önce /me/ dönmesini bekleriz (eksik menü bug'ı).
  const [permsLoaded, setPermsLoaded] = useState<boolean>(
    () => !loadState().isAuthenticated || cached.permissions.length > 0,
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (state.isAuthenticated) {
      apiClient.get("/api/auth/me/")
        .then((r) => {
          const perms = r.data.permissions || [];
          const hidden = r.data.menu_hidden || [];
          setPermissions(perms);
          setMenuHidden(hidden);
          localStorage.setItem("auth_permissions", JSON.stringify(perms));
          localStorage.setItem("auth_menu_hidden", JSON.stringify(hidden));
        })
        .catch(() => {
          // Hata: cache varsa onu koru; yoksa boş. Her hâlükârda yüklemeyi bitir.
        })
        .finally(() => setPermsLoaded(true));
    } else {
      setPermsLoaded(true);
    }
  }, [state.isAuthenticated, state.tenant?.id]);

  async function login(username: string, password: string, tenantSlug?: string, app?: string) {
    const res = await apiClient.post("/api/auth/login/", {
      username, password, tenant_slug: tenantSlug, app,
    });
    persistAuth(res.data);
    setState(loadState());
  }

  function logout() {
    // Refresh token'ı sunucuda blacklist'e al (best-effort; impersonation'da
    // refresh yoktur, o zaman atlanır). Yerel oturum her hâlükârda kapanır.
    const refresh = localStorage.getItem("refresh_token");
    if (refresh) {
      apiClient.post("/api/auth/logout/", { refresh }).catch(() => {});
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem("auth_tenant");
    localStorage.removeItem("auth_branch");
    localStorage.removeItem("auth_role");
    localStorage.removeItem("auth_memberships");
    localStorage.removeItem("auth_permissions");
    localStorage.removeItem("auth_menu_hidden");
    setPermissions([]);
    setMenuHidden([]);
    queryClient.clear();
    setState(loadState());
  }

  async function switchTenant(tenantSlug: string) {
    const member = state.memberships.find((m) => m.tenant_slug === tenantSlug);
    if (!member) throw new Error("Bu tenant'a üyeliğin yok");
    // Sadece username + tenant_slug ile yeniden login için password gerekli.
    // Pratik çözüm: refresh token'a tenant claim'i değiştirmek backend'de
    // ayrı endpoint gerektirir. MVP: logout + redirect to /login?tenant=
    logout();
    window.location.href = `/login?tenant=${tenantSlug}`;
  }

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state, permissions, menuHidden, login, logout, switchTenant,
      // Auth var ama yetkiler henüz yüklenmediyse loading — AppShell bu sırada
      // menüyü çizmez (eksik menü flicker'ı önlenir).
      isLoading: state.isAuthenticated && !permsLoaded,
    }),
    [state, permissions, menuHidden, permsLoaded],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/**
 * Impersonation bootstrap — Orderium Console'dan "restoran olarak giriş" ile
 * gelen kısa ömürlü auth payload'ını URL hash'inden (#impersonate=...) yakalar,
 * localStorage'a yazar (mevcut oturumun üzerine) ve hash'i temizler. Hash
 * sunucuya gitmez → token access loglarına düşmez. İlk loadState çağrısından
 * önce (modül yüklenince) bir kez çalışır.
 */
function consumeImpersonationFromHash(): void {
  if (typeof window === "undefined" || !window.location.hash) return;
  const m = window.location.hash.match(/(?:^#|&)impersonate=([^&]+)/);
  if (!m) return;
  try {
    const data = JSON.parse(decodeURIComponent(atob(m[1])));
    if (data && data.access && data.tenant) {
      persistAuth(data);
    }
  } catch {
    // Bozuk payload — yok say (normal login akışı bozulmaz).
  } finally {
    // Token'ı URL'den temizle (geçmiş/paylaşım sızıntısını önle).
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

consumeImpersonationFromHash();

export function loadState(): AuthState {
  const token = localStorage.getItem("auth_token");
  if (!token) {
    return {
      user: null, tenant: null, branch: null, role: null,
      memberships: [], isAuthenticated: false,
    };
  }
  return {
    user: safeParse(localStorage.getItem("auth_user")),
    tenant: safeParse(localStorage.getItem("auth_tenant")),
    branch: safeParse(localStorage.getItem("auth_branch")),
    role: localStorage.getItem("auth_role"),
    memberships: safeParse(localStorage.getItem("auth_memberships")) || [],
    isAuthenticated: true,
  };
}

function persistAuth(data: any) {
  localStorage.setItem("auth_token", data.access);
  // Impersonation'da refresh yok (null) — yenilenemez kısa oturum. "null"
  // string yazmamak için temizle: access süresi dolunca temiz logout olur.
  if (data.refresh) {
    localStorage.setItem("refresh_token", data.refresh);
  } else {
    localStorage.removeItem("refresh_token");
  }
  localStorage.setItem("auth_user", JSON.stringify(data.user));
  localStorage.setItem("auth_tenant", JSON.stringify(data.tenant));
  localStorage.setItem("auth_branch", JSON.stringify(data.branch));
  localStorage.setItem("auth_role", data.role);
  localStorage.setItem("auth_memberships", JSON.stringify(data.memberships));
}

function safeParse<T>(s: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/**
 * RoleGate — RBAC view-layer.
 *
 * <RoleGate roles={["OWNER", "MANAGER"]}>
 *   <SensitiveButton />
 * </RoleGate>
 */
interface RoleGateProps {
  roles?: string[];
  permission?: { resource: string; action: string };
  fallback?: ReactNode;
  children: ReactNode;
}

export function RoleGate({ roles, permission, fallback = null, children }: RoleGateProps) {
  const { role, permissions } = useAuth();

  if (roles && roles.length > 0) {
    if (!role || !roles.includes(role)) return <>{fallback}</>;
  }

  if (permission) {
    const found = permissions.find((p) => p.resource === permission.resource);
    if (!found || !found.actions.includes(permission.action)) {
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
}

/**
 * ProtectedRoute — auth gerektiren sayfalar için wrapper.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    window.location.href = "/login";
    return null;
  }
  return <>{children}</>;
}
